import { useEffect, useMemo, useRef, useState } from 'react';
import { useKeyboardLayout } from '../utils/useKeyboardLayout';
import './HierarchicalSlider.css';

// Design agent: Spec 009 — how long the "elastic settle" bounce plays after a handle is released.
// Kept as one named constant so the JS timeout that clears the animation class and the CSS
// `animation-duration` it pairs with (see HierarchicalSlider.css) can never drift out of sync.
const SETTLE_ANIMATION_MS = 120;

const TRACK_MAX_PREFIX = 32;
const LAYER_COLORS = [
  'var(--layer-color-a)',
  'var(--layer-color-b)',
  'var(--layer-color-c)',
  'var(--layer-color-d)',
];
// Design agent: Converts a CIDR prefix into a percentage along the slider track.
const prefixToPercent = (prefix, supernetPrefix) => {
  const range = Math.max(0.0001, TRACK_MAX_PREFIX - supernetPrefix);
  return ((prefix - supernetPrefix) / range) * 100;
};

// Design agent: Retrieves a handle descriptor by id in a robust way.
const getHandleById = (handles, id) => handles.find((handle) => handle.id === id);

// Design agent: Produces the prefix that should paint the track for a handle, preferring live drag data.
const resolvePaintPrefix = (handle, dragState, supernetPrefix) => {
  if (!dragState || dragState.id !== handle.id) {
    return handle.prefix;
  }
  return Math.min(TRACK_MAX_PREFIX, Math.max(supernetPrefix, dragState.visualPrefix));
};

// Design agent: Renders a multi-handle slider that maps CIDR prefixes to coloured segments.
function HierarchicalSlider({
  supernetPrefix,
  handles = [],
  magnets,
  onHandleChange,
  onAddLayer,
  onRemoveLayer,
  canAdd,
  canRemove,
  disabled,
  // Design agent: Spec 009 — 'inline' is the compact card usage, 'zoom' is the same component
  // rendered full-width inside SliderZoomOverlay. Both variants share state and handlers; only
  // the outer class changes so the CSS can grow the track/handles for the fullscreen view.
  variant = 'inline',
}) {
  const railRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  // Design agent: Maintains live hover tooltip metadata for network availability feedback.
  const [hoverState, setHoverState] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    label: '',
  });
  // Design agent: Spec 009 — tracks which handle the pointer is currently over (independent from
  // dragging) so the track segment it would resize can get a subtle "about to move" scale cue.
  const [hoveredHandleId, setHoveredHandleId] = useState(null);
  // Design agent: Spec 009 — names the handle currently playing its release "settle" animation.
  // Cleared automatically after SETTLE_ANIMATION_MS so the CSS class (and therefore the
  // keyframe animation) never lingers past one playthrough.
  const [settlingHandleId, setSettlingHandleId] = useState(null);
  const settleTimeoutRef = useRef(null);
  // Design agent: Spec 009 — keyboard navigation mirrors h/l or j/m depending on the detected
  // physical keyboard layout (see the spec's "jklm" note), reusing the hook spec 008 already
  // introduced for the method-toggle shortcut hints instead of inventing a second detector.
  const detectedKeyboardLayout = useKeyboardLayout();

  // Developer agent: Clears any pending settle-animation timeout on unmount so it never fires a
  // state update after the component is gone.
  useEffect(() => {
    return () => {
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
    };
  }, []);

  // Design agent: Ensures magnet anchors stay ordered for predictable snapping.
  const magnetPoints = useMemo(() => {
    if (!magnets || magnets.length === 0) {
      return [];
    }
    return [...magnets].sort((a, b) => a - b);
  }, [magnets]);

  // Design agent: Builds logical segments so the hover tooltip can expose network and host counts.
  const segmentMetrics = useMemo(() => {
    const range = Math.max(0.0001, TRACK_MAX_PREFIX - supernetPrefix);
    const segments = [];
    const paintSegments = [];
    const sorted = [...handles].sort((a, b) => a.prefix - b.prefix);
    let previousPrefix = supernetPrefix;
    let previousPercent = 0;

    sorted.forEach((handle, index) => {
      const paintPrefix = resolvePaintPrefix(handle, dragState, supernetPrefix);
      const boundedPrefix = Math.min(TRACK_MAX_PREFIX, Math.max(previousPrefix, paintPrefix));
      const bits = Math.max(0, boundedPrefix - previousPrefix);
      const nextPercent = Math.max(previousPercent, ((boundedPrefix - supernetPrefix) / range) * 100);
      const rawLabel = handle.label ? handle.label.trim() : '';
      const label = rawLabel !== '' ? rawLabel : `Layer ${index + 1}`;
      const segmentStart = previousPercent;
      const segmentEnd = Math.min(100, nextPercent);
      segments.push({
        type: 'layer',
        id: handle.id,
        index,
        label,
        startPercent: previousPercent,
        endPercent: segmentEnd,
        networkCount: Math.max(1, 2 ** bits),
      });
      paintSegments.push({
        id: handle.id,
        color: LAYER_COLORS[index % LAYER_COLORS.length],
        startPercent: segmentStart,
        endPercent: segmentEnd,
      });
      previousPrefix = boundedPrefix;
      previousPercent = nextPercent;
    });

    const remainderBits = Math.max(0, TRACK_MAX_PREFIX - previousPrefix);
    const hostAddresses = Math.max(1, 2 ** remainderBits);
    segments.push({
      type: 'host',
      startPercent: previousPercent,
      endPercent: 100,
      hostAddresses,
    });

    return { range, segments, paintSegments };
  }, [dragState, handles, supernetPrefix]);

  // Design agent: Builds tick marks that highlight key CIDR checkpoints.
  const scaleMarks = useMemo(() => {
    const marks = new Set([Math.round(supernetPrefix), TRACK_MAX_PREFIX]);
    magnetPoints.forEach((point) => {
      if (point >= supernetPrefix && point <= TRACK_MAX_PREFIX) {
        marks.add(Math.round(point));
      }
    });
    return [...marks].sort((a, b) => a - b);
  }, [magnetPoints, supernetPrefix]);

  // Design agent: Handles pointer movement for draggable slider handles.
  useEffect(() => {
    if (!dragState || disabled) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      if (!railRef.current) {
        return;
      }

      const rect = railRef.current.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }

      const relative = (event.clientX - rect.left) / rect.width;
      const clampedRatio = Math.min(1, Math.max(0, relative));
      const rawPrefix = supernetPrefix + clampedRatio * (TRACK_MAX_PREFIX - supernetPrefix);

      const now = event.timeStamp || performance.now();
      const elapsed = Math.max(1, now - dragState.lastTime);
      const delta = Math.abs(rawPrefix - dragState.visualPrefix);
      const speed = (delta / elapsed) * 1000;
      const baseThreshold = Math.max(0.04, (speed ** 1.15) * 0.1);
      const slowReduction = speed < 0.85 ? (0.85 - speed) * 0.32 : 0;
      const magnetThreshold = Math.min(1.5, Math.max(0.04, baseThreshold - slowReduction));

      let candidate = rawPrefix;
      if (magnetPoints.length > 0) {
        let snapped = candidate;
        let closest = magnetThreshold + 1;
        magnetPoints.forEach((point) => {
          const distance = Math.abs(point - candidate);
          if (distance <= magnetThreshold && distance < closest) {
            snapped = point;
            closest = distance;
          }
        });
        candidate = snapped;
      }

      const bounded = Math.min(TRACK_MAX_PREFIX, Math.max(supernetPrefix, candidate));
      const rounded = Math.round(bounded);

      if (rounded !== dragState.lastReportedPrefix) {
        onHandleChange(dragState.id, rounded);
      }

      setDragState((previous) => {
        if (!previous || previous.id !== dragState.id) {
          return previous;
        }
        return {
          ...previous,
          visualPrefix: bounded,
          lastTime: now,
          lastReportedPrefix: rounded,
        };
      });
    };

    // Design agent: Spec 009 — releasing the handle is exactly when the "elastic settle" should
    // play: the handle jumps from its continuous 1:1 drag position to the nearest whole-bit
    // prefix, so a short overshoot animation on that jump reads as a spring settling into place
    // rather than an abrupt snap. CSS silences the animation under prefers-reduced-motion, so this
    // class toggle is unconditional here.
    const handlePointerUp = () => {
      const releasedHandleId = dragState.id;
      setDragState(null);
      setSettlingHandleId(releasedHandleId);
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
      settleTimeoutRef.current = setTimeout(() => {
        setSettlingHandleId(null);
        settleTimeoutRef.current = null;
      }, SETTLE_ANIMATION_MS);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [disabled, dragState, magnetPoints, onHandleChange, supernetPrefix]);

  // Design agent: Maintains handle ordering for consistent layout.
  const sortedHandles = useMemo(
    () => [...handles].sort((a, b) => a.prefix - b.prefix),
    [handles],
  );

  // Design agent: Captures pointer input and initialises dragging metadata.
  const beginDrag = (event, handle) => {
    if (disabled) {
      return;
    }
    event.preventDefault();
    const handleRecord = getHandleById(handles, handle.id);
    const handlePrefix = handleRecord ? handleRecord.prefix : handle.prefix;
    setDragState({
      id: handle.id,
      visualPrefix: handlePrefix,
      lastReportedPrefix: handlePrefix,
      lastTime: event.timeStamp || performance.now(),
    });
  };

  // Design agent: Spec 009 — lets a focused handle be moved entirely from the keyboard: arrow keys
  // move it by one bit, Shift+arrow by four, and the same physical keys used for vim-style left/
  // right navigation (h/l on QWERTY, j/m on AZERTY per the owner's "jklm" note) mirror the arrows
  // so a keyboard-only user never has to reach for the mouse to fine-tune a boundary.
  // Developer agent: Reads `event.key` (the character, not the physical `event.code`) because the
  // mirrored letters are meant to match what the user actually types on their detected layout —
  // unlike the Alt+1/2/M shortcuts in VlsmCalculator.js, which intentionally use `event.code` to
  // stay pinned to a physical key regardless of layout.
  const handleHandleKeyDown = (event, handle) => {
    if (disabled) {
      return;
    }
    const bitStep = event.shiftKey ? 4 : 1;
    let direction = 0;
    if (event.key === 'ArrowRight') {
      direction = 1;
    } else if (event.key === 'ArrowLeft') {
      direction = -1;
    } else {
      const typedKey = event.key.toLowerCase();
      const isAzerty = detectedKeyboardLayout === 'fr-fr';
      const leftKey = isAzerty ? 'j' : 'h';
      const rightKey = isAzerty ? 'm' : 'l';
      if (typedKey === leftKey) {
        direction = -1;
      } else if (typedKey === rightKey) {
        direction = 1;
      }
    }

    if (direction === 0) {
      return;
    }
    event.preventDefault();

    const handleRecord = getHandleById(handles, handle.id);
    const currentPrefix = handleRecord ? handleRecord.prefix : handle.prefix;
    const nextPrefix = Math.min(
      TRACK_MAX_PREFIX,
      Math.max(supernetPrefix, Math.round(currentPrefix) + direction * bitStep),
    );
    onHandleChange(handle.id, nextPrefix);
  };

  // Design agent: Computes the tooltip text and coordinates while hovering over the slider rail.
  const handleRailHover = (event) => {
    if (!railRef.current || disabled) {
      setHoverState((previous) =>
        previous.isVisible
          ? {
            ...previous,
            isVisible: false,
          }
          : previous,
      );
      return;
    }
    const rect = railRef.current.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const relativeX = Math.min(rect.width, Math.max(0, event.clientX - rect.left));
    const ratioPercent = rect.width === 0 ? 0 : (relativeX / rect.width) * 100;
    const segment = segmentMetrics.segments.find(
      (candidate) => ratioPercent >= candidate.startPercent && ratioPercent <= candidate.endPercent,
    );
    if (!segment) {
      setHoverState((previous) =>
        previous.isVisible
          ? {
            ...previous,
            isVisible: false,
          }
          : previous,
      );
      return;
    }
    let label;
    if (segment.type === 'layer') {
      label = `${segment.label}: ${segment.networkCount.toLocaleString()} network${segment.networkCount === 1 ? '' : 's'
        }`;
    } else {
      label = `Host pool: ${segment.hostAddresses.toLocaleString()} available hosts`;
    }
    setHoverState({
      isVisible: true,
      x: relativeX,
      y: rect.height / 2,
      label,
    });
  };

  // Design agent: Hides the tooltip when the pointer exits the slider rail.
  const handleRailLeave = () => {
    setHoverState((previous) =>
      previous.isVisible
        ? {
          ...previous,
          isVisible: false,
        }
        : previous,
    );
  };

  // Design agent: Prevents stale tooltips from lingering when the slider is disabled.
  useEffect(() => {
    if (!disabled) {
      return;
    }
    setHoverState((previous) =>
      previous.isVisible
        ? {
          ...previous,
          isVisible: false,
        }
        : previous,
    );
  }, [disabled]);

  return (
    <div
      className={`hierarchical-slider${disabled ? ' is-disabled' : ''}${
        variant === 'zoom' ? ' hierarchical-slider--zoom' : ''
      }`}
    >
      <div className="slider-toolbar">
        <span className="slider-title">Layer distribution</span>
        <div className="slider-actions" tabIndex={0}>
          <button
            type="button"
            className={`ghost-button slider-button${canRemove ? '' : ' slider-button--placeholder'}`}
            onClick={onRemoveLayer}
            disabled={!canRemove || disabled}
            aria-label="Remove last layer"
            aria-hidden={canRemove ? undefined : true}
            tabIndex={canRemove ? 0 : -1}
          >
            –
          </button>
          <button
            type="button"
            className="ghost-button slider-button"
            onClick={onAddLayer}
            disabled={!canAdd || disabled}
            aria-label="Add layer"
          >
            +
          </button>
        </div>
      </div>
      <div className="slider-track">
        <div className="slider-rail">
          <div
            className="slider-rail-track"
            ref={railRef}
            onMouseMove={handleRailHover}
            onMouseEnter={handleRailHover}
            onMouseLeave={handleRailLeave}
          >
            <div className="slider-track-fill">
              {segmentMetrics.paintSegments.map((segment) => {
                const widthPercent = Math.max(0, segment.endPercent - segment.startPercent);
                // Design agent: Spec 009 — the segment tied to the handle that is being dragged
                // (or merely hovered) is "the track section being resized", so it gets the elastic
                // scaleY(1.06) cue; every other segment stays untouched.
                const isResizingSegment = dragState
                  ? dragState.id === segment.id
                  : hoveredHandleId === segment.id;
                return (
                  <div
                    key={`paint-${segment.id}`}
                    className={`slider-track-segment${isResizingSegment ? ' slider-track-segment--active' : ''}`}
                    style={{
                      left: `${segment.startPercent}%`,
                      width: `${widthPercent}%`,
                      backgroundColor: segment.color,
                    }}
                  />
                );
              })}
            </div>
            <div className="slider-track-overlay" />
            {sortedHandles.map((handle, index) => {
              const trimmedLabel = handle.label ? handle.label.trim() : '';
              const handleLabel = trimmedLabel !== '' ? trimmedLabel : `Layer ${index + 1}`;
              const activePercent =
                dragState && dragState.id === handle.id
                  ? prefixToPercent(dragState.visualPrefix, supernetPrefix)
                  : prefixToPercent(handle.prefix, supernetPrefix);
              const activeLabel =
                dragState && dragState.id === handle.id
                  ? Math.round(Math.min(TRACK_MAX_PREFIX, Math.max(supernetPrefix, dragState.visualPrefix)))
                  : handle.prefix;
              const isSettling = settlingHandleId === handle.id;
              return (
                <button
                  type="button"
                  key={handle.id}
                  id={`hierarchical-slider-handle-${handle.id}`}
                  className={`slider-handle${isSettling ? ' slider-handle--settling' : ''}`}
                  style={{ left: `${activePercent}%` }}
                  onPointerDown={(event) => beginDrag(event, handle)}
                  onKeyDown={(event) => handleHandleKeyDown(event, handle)}
                  onMouseEnter={() => setHoveredHandleId(handle.id)}
                  onMouseLeave={() =>
                    setHoveredHandleId((previous) => (previous === handle.id ? null : previous))
                  }
                  disabled={disabled}
                  aria-label={`${handleLabel} boundary set to /${activeLabel}`}
                  // Design agent: Spec 009 — exposes the handle as a standard ARIA slider so screen
                  // readers announce its current/min/max prefix, matching the acceptance criteria.
                  role="slider"
                  aria-valuenow={activeLabel}
                  aria-valuemin={supernetPrefix}
                  aria-valuemax={TRACK_MAX_PREFIX}
                >
                  <span className="slider-handle-label">/{activeLabel}</span>
                </button>
              );
            })}
            {hoverState.isVisible && (
              <div
                className="slider-hover-tooltip"
                style={{
                  left: `${hoverState.x}px`,
                  top: `${hoverState.y}px`,
                }}
              >
                {hoverState.label}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="slider-scale">
        {scaleMarks.map((mark) => (
          <span key={`mark-${mark}`} className="slider-scale-mark">
            /{mark}
          </span>
        ))}
      </div>
    </div>
  );
}

export default HierarchicalSlider;
// Design agent: Also export named for import flexibility and IDE refactors.
export { HierarchicalSlider };
